import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { EditorContext } from "../containers/editor/EditorLayout";
import {
  customRound,
  floatToFloatSec,
  getHMSToSec,
  operateStorageData,
  playMedia,
  removeMultiAttrs,
  setMultiAttrs,
  stringTocamelCase,
} from "../_helpers/utils";
import {
  BRAND,
  COLLAGE,
  DOCUMENT_PLAY_DATA_STORAGE_KEY,
  FRAME,
  PAGE_PLAY_DATA_STORAGE_KEY,
  PAGE_TIMELINE_LEFT_SECTION,
  STOCK,
  UPLOAD_VIDEO,
  VIDEO,
} from "../constants/editor";
import { EditorWrapperContext } from "../containers/editor/EditorWrapper";

let intervalId;

const animationEngine = {
  meta: {
    speedToDurationMsObj: {
      Fast: 500,
      Medium: 1000,
      Slow: 2000,
    },
  },

  text: {
    meta: {
      entryTypes: ["Flip In X", "Flip In Y", "Bounce In", "Rotate In", "Roll In"],
      exitTypes: ["Flip Out X", "Flip Out Y", "Bounce Out", "Rotate Out", "Roll Out"],
      entryPresets: {
        flipInX: { name: "flipInX", cls: "flip-in-x", duration: 0.8, ease: "ease-out" },
        flipInY: { name: "flipInY", cls: "flip-letter-y", duration: 0.8, ease: "ease-out" },
        bounceIn: { name: "bounceIn", cls: "bounce-in-letter", duration: 0.9 },
        rotateIn: { name: "rotateIn", cls: "rotate-in-letter", duration: 0.8 },
        rollIn: { name: "rollIn", cls: "roll-in-char", duration: 0.8 },
      },
      exitPresets: {
        flipOutX: { name: "flipOutX", cls: "flip-out-char", duration: 0.8, ease: "ease-in" },
        flipOutY: { name: "flipOutY", cls: "flip-letter-y", duration: 0.8, ease: "ease-in" },
        bounceOut: { name: "bounceOut", cls: "bounce-out-letter", duration: 0.9 },
        rotateOut: { name: "rotateOut", cls: "rotate-out-letter", duration: 0.8 },
        rollOut: { name: "rollOut", cls: "roll-out-char", duration: 0.7 },
      },
    },

    beforeWrap: ({ el }) => {
      // ensure perspective wrapper
      if (!el.parentNode.classList.contains("flip-y-root")) {
        const wrapper = document.createElement("div");
        wrapper.className = "flip-y-root";
        el.parentNode.insertBefore(wrapper, el);
        wrapper.appendChild(el);
      }
      // clear old animation spans
      el.parentNode.querySelectorAll(".flip-letter-y").forEach(n => n.remove());
      return el;
    },

    afterRestore: ({ el }) => {
      const root = el.parentNode;
      if (root && root.classList.contains("flip-y-root")) {
        root.replaceWith(...root.childNodes);
      }
    },

    // wrapTextNodes: ({ node, preset }) => {
    //   const _aEngine = animationEngine.text;
    //   [...node.childNodes].forEach(child => {
    //     if (child.nodeType === 3 && child.textContent.trim().length > 0) {
    //       const fragment = document.createDocumentFragment();
    //       [...child.textContent].forEach(char => {
    //         if (char === "\n") return;
    //         const span = document.createElement("span");
    //         span.className = preset.charClass;
    //         span.innerHTML = char === " " ? "&nbsp;" : char;
    //         fragment.appendChild(span);
    //       });
    //       child.replaceWith(fragment);
    //     } else if (child.nodeType === 1) {
    //       _aEngine.wrapTextNodes({ node: child, preset });
    //     }
    //   });
    // },

    wrapTextNodes: ({ node, preset }) => {
      const _aEngine = animationEngine.text;
      [...node.childNodes].forEach(child => {
        if (child.nodeType === 3 && child.textContent.trim().length > 0) {
          const fragment = document.createDocumentFragment();
          const words = child.textContent.split(" ");

          words.forEach((word, wordIndex) => {
            const wordSpan = document.createElement("span");
            wordSpan.style.display = "inline-block"; // 🔥 IMPORTANT
            wordSpan.style.whiteSpace = "nowrap"; // 🔥 PREVENT BREAK

            [...word].forEach(char => {
              const charSpan = document.createElement("span");
              charSpan.className = preset.charClass;
              charSpan.innerHTML = char === " " ? "&nbsp;" : char;
              wordSpan.appendChild(charSpan);
            });

            fragment.appendChild(wordSpan);

            // add space after word (except last)
            if (wordIndex !== words.length - 1) {
              fragment.appendChild(document.createTextNode(" "));
            }
          });

          child.replaceWith(fragment);
        } else if (child.nodeType === 1) {
          _aEngine.wrapTextNodes({ node: child, preset });
        }
      });
    },

    getAnimationPreset: ({ durationMs, animationClass }) => {
      const { meta, ..._aEngine } = animationEngine.text;
      const animationObj = meta.entryPresets?.[animationClass] || meta.exitPresets?.[animationClass];
      if (!animationObj) return;

      const specialType = ["flipInY", "flipOutY"].includes(animationClass);
      const preset = {
        animationName: animationObj.name,
        charClass: animationObj.cls,
        duration: animationObj.duration,
        ease: animationObj?.ease || "ease-out",
        maxTotalTime: durationMs / 1000,
        beforeWrap: specialType ? _aEngine.beforeWrap : null,
        afterRestore: specialType ? _aEngine.afterRestore : null,
      };
      return preset;
    },

    getPrerequisites: ({ animationClass, durationMs, widgetNode }) => {
      const _aEngine = animationEngine.text;
      return {
        el: widgetNode.querySelector(".primary-text-widget"),
        preset: _aEngine.getAnimationPreset({ durationMs, animationClass }),
        dataset: widgetNode.dataset,
      };
    },

    getSanitizedInnerHtml: ({ el, dataset }) => {
      return !dataset.innerHtml || dataset.innerHtml === "undefined" ? el.innerHTML : dataset.innerHtml;
    },

    start: ({ phase, widgetNode }) => {
      const _aEngine = animationEngine.text;
      const { el, preset, dataset } = _aEngine.getPrerequisites({
        animationClass: phase.animationClass,
        durationMs: phase.durationMs,
        widgetNode,
      });
      if (!el || !preset || dataset.animationPhase === phase.phaseId) return;

      // Store data in DOM
      setMultiAttrs(widgetNode, {
        "data-inner-html": _aEngine.getSanitizedInnerHtml({ el, dataset }),
        "data-animation-phase": phase.phaseId,
      });

      // Prepare DOM
      const context = preset?.beforeWrap?.({ el }) || el;
      _aEngine.wrapTextNodes({ node: context, preset });

      const chars = context.querySelectorAll(`.${preset.charClass}`);
      const charCount = chars.length;
      if (!charCount) return;

      // Calculate timing
      const animDuration = Math.min(preset.duration, preset.maxTotalTime);
      const perCharDelay = charCount > 1 ? Math.max((preset.maxTotalTime - animDuration) / (charCount - 1), 0) : 0;

      // Apply animations
      chars.forEach((span, i) => {
        span.classList.add("play-animation");
        span.style.animation = `
        ${preset.animationName} ${animDuration}s ${i * perCharDelay}s both ${preset.ease}
      `;
      });
    },

    continue: ({ phase, timer, widgetNode }) => {
      widgetNode.setAttribute(phase.progressKey, timer - phase.progressOffset);
    },

    stop: ({ phase, widgetNode }) => {
      const _aEngine = animationEngine.text;
      const { el, preset, dataset } = _aEngine.getPrerequisites({
        animationClass: phase.animationClass,
        durationMs: phase.durationMs,
        widgetNode,
      });
      if (!el || !preset) return;

      el.innerHTML = _aEngine.getSanitizedInnerHtml({ el, dataset });
      preset?.afterRestore?.({ el });
      removeMultiAttrs(widgetNode, [
        "data-inner-html",
        "data-animation-phase",
        "data-entry-animation-played-duration",
        "data-exit-animation-played-duration",
      ]);
    },

    seek: ({ phase, timer, widgetNode }) => {
      const _aEngine = animationEngine.text;
      const { el, preset, dataset } = _aEngine.getPrerequisites({
        animationClass: phase.animationClass,
        durationMs: phase.durationMs,
        widgetNode,
      });
      if (!el || !preset) return;

      // Prepare DOM once (if condition not meet then it's guranteed that DOM is already prepared)
      if (dataset.animationPhase !== phase.phaseId) {
        _aEngine.start({
          phase,
          widgetNode,
        });
      }

      const chars = el.querySelectorAll(`.${preset.charClass}`);
      const charCount = chars.length;
      if (!charCount) return;

      // Calculate timing
      const animDuration = Math.min(preset.duration, preset.maxTotalTime);
      const perCharDelay = charCount > 1 ? Math.max((preset.maxTotalTime - animDuration) / (charCount - 1), 0) : 0;
      const t = (timer - phase.startTime) / 1000; // current phase time

      // Apply animations
      chars.forEach((span, i) => {
        const off = t - i * perCharDelay; // offset time is current phase time − this character’s start delay
        span.style.animationPlayState = "paused";
        span.style.animationDelay = off <= 0 ? `${-off}s` : off >= animDuration ? `-${animDuration}s` : `-${off}s`;
      });
    },
  },

  widget: {
    getAnimationNode: ({ phase, widgetNode }) => {
      if (phase.engine !== "widget") return;

      // detecting animation node
      let animationNode = widgetNode.querySelector(".animation-node");

      // create animation node if not exists (scenario: playing from beginning)
      if (!animationNode) {
        animationNode = document.createElement("div");
        animationNode.classList.add("animation-node");
        animationNode.style.height = "100%";
        animationNode.style.width = "100%";
        widgetNode.childNodes.forEach(child => animationNode.append(child));
        widgetNode.innerHTML = "";
        widgetNode.append(animationNode);
      }
      return animationNode;
    },

    start: ({ phase, widgetNode }) => {
      const _aEngine = animationEngine.widget;
      const animationNode = _aEngine.getAnimationNode({ phase, widgetNode });
      if (!widgetNode || !animationNode) return;

      widgetNode.setAttribute("data-animation-phase", phase.phaseId);
      animationNode.classList.add("play-animation");
      animationNode.classList.add(phase.animationClass);
      animationNode.style.animationDuration = `${phase.durationMs}ms`;
    },

    continue: ({ phase, timer, widgetNode }) => {
      const _aEngine = animationEngine.widget;
      const animationNode = _aEngine.getAnimationNode({ phase, widgetNode });
      if (!animationNode) return;

      animationNode.setAttribute(phase.progressKey, timer - phase.progressOffset);
    },

    stop: ({ phase, widgetNode }) => {
      const _aEngine = animationEngine.widget;
      const animationNode = _aEngine.getAnimationNode({ phase, widgetNode });
      if (!widgetNode || !animationNode) return;

      const widgetChildNodes = animationNode.childNodes;
      widgetNode.removeAttribute("data-animation-phase");
      widgetNode.innerHTML = "";
      widgetChildNodes.forEach(n => widgetNode.appendChild(n)); // replacing animation node with original node(s)
    },

    seek: ({ phase, timer, widgetNode }) => {
      const _aEngine = animationEngine.widget;
      const animationNode = _aEngine.getAnimationNode({ phase, widgetNode });
      if (!widgetNode || !animationNode) return;

      const currentMs = timer - phase.startTime;
      const playedMs = parseInt(animationNode.getAttribute(phase.progressKey) ?? 0);

      // apply animation frame
      widgetNode.setAttribute("data-animation-phase", phase.phaseId);
      animationNode.classList.add("play-animation");
      animationNode.classList.add(phase.animationClass);
      animationNode.style.animationDuration = `${phase.durationMs}ms`;
      animationNode.style.animationPlayState = "paused";
      animationNode.style.animationDelay =
        currentMs > playedMs ? `-${currentMs - playedMs}ms` : `${playedMs - currentMs}ms`;
    },
  },

  page: {
    getAnimationNode: ({ phase, pageObj }) => {
      if (phase.engine !== "page") return;

      const pageNode = document.getElementById(pageObj.pageId);
      const blockNode = pageNode.querySelector(".dhp-page-block");

      // detecting wrapper/animation node
      let wrapperNode = pageNode.querySelector(".wrapper-node");
      let animationNode = pageNode.querySelector(".animation-node");

      // create wrapper/animation node if not exists (scenario: playing from beginning)
      if (!wrapperNode || !animationNode) {
        animationNode = document.createElement("div");
        animationNode.classList.add("animation-node");

        wrapperNode = document.createElement("div");
        wrapperNode.classList.add("wrapper-node");
        wrapperNode.style.overflow = "hidden";
        wrapperNode.style.height = "100%";
        wrapperNode.style.width = "100%";
        wrapperNode.appendChild(animationNode);

        blockNode.replaceWith(wrapperNode);
        wrapperNode.childNodes[0].appendChild(blockNode);
      }
      return { pageNode, blockNode, wrapperNode, animationNode };
    },

    start: ({ phase, pageObj }) => {
      const _aEngine = animationEngine.page;
      const { pageNode, animationNode } = _aEngine.getAnimationNode({ phase, pageObj });
      if (!pageNode || !animationNode) return;

      pageNode.setAttribute("data-animation-phase", phase.phaseId);
      animationNode.classList.add("play-animation");
      animationNode.classList.add(phase.animationClass);
      animationNode.style.animationDuration = `${phase.durationMs}ms`;
    },

    continue: ({ phase, timer, pageObj }) => {
      const _aEngine = animationEngine.page;
      const { animationNode } = _aEngine.getAnimationNode({ phase, pageObj });
      if (!animationNode) return;

      animationNode.setAttribute(phase.progressKey, timer - phase.progressOffset);
    },

    stop: ({ phase, pageObj }) => {
      const _aEngine = animationEngine.page;
      const { pageNode, blockNode, wrapperNode } = _aEngine.getAnimationNode({ phase, pageObj });
      if (!pageNode || !blockNode || !wrapperNode) return;

      pageNode.removeAttribute("data-animation-phase");
      wrapperNode.replaceWith(blockNode); // removing wrapper/animation node (replacing with original node)
    },

    seek: ({ phase, timer, pageObj }) => {
      const _aEngine = animationEngine.page;
      const { pageNode, animationNode } = _aEngine.getAnimationNode({ phase, pageObj });
      if (!pageNode || !animationNode) return;

      const currentMs = timer - phase.startTime;
      const playedMs = parseInt(animationNode.getAttribute(phase.progressKey) ?? 0);

      // apply animation frame
      pageNode.setAttribute("data-animation-phase", phase.phaseId);
      animationNode.classList.add("play-animation");
      animationNode.classList.add(phase.animationClass);
      animationNode.style.animationDuration = `${phase.durationMs}ms`;
      animationNode.style.animationPlayState = "paused";
      animationNode.style.animationDelay =
        currentMs > playedMs ? `-${currentMs - playedMs}ms` : `${playedMs - currentMs}ms`;
    },
  },

  utils: {
    getWidgetAnimData: ({ widgetObj, startTimeMs, stopTimeMs, widgetNode }) => {
      const speedToDurationMsObj = animationEngine.meta.speedToDurationMsObj;
      const { entryTypes: textEntryTypes, exitTypes: textExitTypes } = animationEngine.text.meta;

      const showEntryAnimation = Boolean(widgetObj.data["data-entrance-animation"]);
      const entryAnimationSpeed = widgetObj.data["data-entrance-animation-speed"];
      const entryAnimationType = widgetObj.data["data-entrance-animation-type"];
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);
      const entryAnimationDurationMs = showEntryAnimation && speedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationStopTimeMs = showEntryAnimation && startTimeMs + entryAnimationDurationMs;

      const showExitAnimation = Boolean(widgetObj.data["data-exit-animation"]);
      const exitAnimationSpeed = widgetObj.data["data-exit-animation-speed"];
      const exitAnimationType = widgetObj.data["data-exit-animation-type"];
      const exitAnimationClassName = showExitAnimation && stringTocamelCase(exitAnimationType);
      const exitAnimationDurationMs = showExitAnimation && speedToDurationMsObj[exitAnimationSpeed];
      const exitAnimationStartTimeMs = showExitAnimation && stopTimeMs - exitAnimationDurationMs;

      const isTextEntryType = showEntryAnimation && textEntryTypes.includes(entryAnimationType);
      const isTextExitType = showExitAnimation && textExitTypes.includes(exitAnimationType);
      const isWidgetEntryType = showEntryAnimation && !isTextEntryType;
      const isWidgetExitType = showExitAnimation && !isTextExitType;

      const data = {
        showEntryAnimation,
        entryAnimationType,
        entryAnimationClassName,
        entryAnimationDurationMs,
        entryAnimationStartTimeMs: startTimeMs,
        entryAnimationStopTimeMs,
        showExitAnimation,
        exitAnimationType,
        exitAnimationClassName,
        exitAnimationDurationMs,
        exitAnimationStartTimeMs,
        exitAnimationStopTimeMs: stopTimeMs,
        entryEngine: isTextEntryType ? "text" : isWidgetEntryType ? "widget" : false,
        exitEngine: isTextExitType ? "text" : isWidgetExitType ? "widget" : false,
        animationPhase: widgetNode.getAttribute("data-animation-phase"),
      };

      return {
        ...data,
        animDataObj: data,
      };
    },

    getPageAnimData: ({ pageObj, pageDurationMs }) => {
      const pageNode = document.getElementById(pageObj.pageId);
      const blockNode = pageNode.querySelector(".dhp-page-block");
      const speedToDurationMsObj = animationEngine.meta.speedToDurationMsObj;

      const showEntryAnimation = pageObj.pageTransition.enabled;
      const entryAnimationSpeed = pageObj.pageTransition.speed;
      const entryAnimationType = pageObj.pageTransition.type;
      const entryAnimationClassName = showEntryAnimation && stringTocamelCase(entryAnimationType);
      const entryAnimationDurationMs = showEntryAnimation && speedToDurationMsObj[entryAnimationSpeed];
      const entryAnimationStopTimeMs = showEntryAnimation && pageDurationMs;

      const data = {
        showEntryAnimation,
        entryAnimationType,
        entryAnimationClassName,
        entryAnimationDurationMs,
        entryAnimationStartTimeMs: 0,
        entryAnimationStopTimeMs,
        entryEngine: showEntryAnimation ? "page" : false,
        exitEngine: false,
        animationPhase: pageNode.getAttribute("data-animation-phase"),
      };

      return {
        ...data,
        pageNode,
        blockNode,
        animDataObj: data,
      };
    },

    generateAnimPipelines: ({ animDataObj: data }) => {
      const pipelines = [];

      ["text-entry", "text-exit", "widget-entry", "widget-exit", "page-entry"].forEach(pahse => {
        const [animEngine, animType] = pahse.split("-");
        const isEntryTypeEnabled = animEngine === data.entryEngine;
        const isExitTypeEnabled = animEngine === data.exitEngine;

        pipelines.push({
          phaseId: pahse,
          engine: animEngine,
          type: animType,
          enabled: Boolean(animType === "entry" ? isEntryTypeEnabled : isExitTypeEnabled),
          animationType: animType === "entry" ? data.entryAnimationType : data.exitAnimationType,
          animationClass: animType === "entry" ? data.entryAnimationClassName : data.exitAnimationClassName,
          durationMs: animType === "entry" ? data.entryAnimationDurationMs : data.exitAnimationDurationMs,
          startTime: animType === "entry" ? data.entryAnimationStartTimeMs : data.exitAnimationStartTimeMs,
          stopTime: animType === "entry" ? data.entryAnimationStopTimeMs : data.exitAnimationStopTimeMs,
          progressKey: `data-${animType}-animation-played-duration`,
          progressOffset: animType === "entry" ? 0 : data.exitAnimationStartTimeMs,
        });
      });

      return pipelines;
    },

    dispatchAnimation: ({ phase, timer, widgetNode, pageObj }) => {
      const _aEngine = animationEngine[phase?.engine];
      if (!_aEngine) return;

      if (timer === phase.startTime) {
        _aEngine.start({
          phase,
          widgetNode,
          pageObj,
        });
      }

      if (timer > phase.startTime && timer < phase.stopTime) {
        _aEngine.continue({
          phase,
          timer,
          widgetNode,
          pageObj,
        });
      }

      if (timer === phase.stopTime) {
        _aEngine.stop({
          phase,
          widgetNode,
          pageObj,
        });
      }
    },

    seekAnimation: ({ activePhase, deactivePhase, timer, widgetNode, pageObj }) => {
      // outside playable range → RESTORE deactive animation phase (if any)
      if (deactivePhase) {
        const _aEngine = animationEngine[deactivePhase?.engine];
        if (!_aEngine) return;

        _aEngine.stop({
          phase: deactivePhase,
          widgetNode,
          pageObj,
        });
      }

      // within playable range
      if (activePhase) {
        const _aEngine = animationEngine[activePhase?.engine];
        if (!_aEngine) return;

        _aEngine.seek({
          phase: activePhase,
          timer,
          widgetNode,
          pageObj,
        });
      }
    },
  },
};

const usePagePlay = () => {
  const {
    widgets,
    pageNodes,
    metadata,
    updateMetadata,
    isTimeLineViewOpen,
    // handleWidgetAction,
  } = useContext(EditorContext);

  const { pagePlayData, setPagePlayData, playingPageIdx, setPlayingPageIdx, whichPagePlaying, setWhichPagePlaying } =
    useContext(EditorWrapperContext);

  const [action, setAction] = useState(null);

  const getPageData = () => {
    let storage_data;
    if (!pagePlayData.pageId) {
      storage_data = operateStorageData({
        type: "session",
        action: "get",
        key: isTimeLineViewOpen === "page-timeline" ? PAGE_PLAY_DATA_STORAGE_KEY : DOCUMENT_PLAY_DATA_STORAGE_KEY,
        isParseable: true,
        defaultData: {},
      });
      console.log(storage_data);
    }
    const pageId = pagePlayData.pageId ?? storage_data.pageId;
    const pageIdx = parseInt(pagePlayData.pageIdx ?? storage_data.pageIdx);
    const pageObj = pageNodes[pageIdx];
    const pageDurationMs = parseFloat(pageObj?.pageDuration ?? 0) * 1000;

    return {
      pageId,
      pageIdx,
      pageObj,
      pageDurationMs,
      scrollData: getScrollData(),
    };
  };

  const getScrollData = () => {
    const scrollContainer = document.getElementById("timeline-body");
    const count = Math.ceil(scrollContainer.scrollWidth / scrollContainer.offsetWidth);
    const breakPoints = [...Array(count)].map((_, i) => scrollContainer.offsetWidth * i);
    const rangePoints = breakPoints.map((p, i) => {
      const nextPoint = breakPoints[i + 1];
      return {
        min: p - PAGE_TIMELINE_LEFT_SECTION,
        max: (nextPoint ? nextPoint - 1 : scrollContainer.scrollWidth) - PAGE_TIMELINE_LEFT_SECTION,
      };
    });

    return { rangePoints };
  };

  const getWidgetData = ({ widgetObj, pageDurationMs }) => {
    const { startTime = 0, endTime = pageDurationMs } = widgetObj?.duration ?? {};
    const startTimeMs = parseFloat(startTime) * 1000;
    const endTimeMs = parseFloat(endTime) * 1000;
    const stopTimeMs = endTimeMs > pageDurationMs ? pageDurationMs : endTimeMs;
    const widgetNode = document.getElementById(widgetObj.id);
    const assetType = widgetObj.data["data-asset-type"];
    const source = widgetObj.data["data-source"];
    const frameContent = widgetObj.data["data-clipped"];

    return { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent };
  };

  const getVideoNodeAndDataRef = ({ widgetId, widgetNode, videoNode }) => {
    const widget = widgetId ? document.getElementById(widgetId) : widgetNode;
    const assetTypeCollage = widget.dataset.assetType === COLLAGE;
    const video = assetTypeCollage ? videoNode : widget?.querySelector("video");
    const { duration: durationInHMS, volume, trim, loop } = assetTypeCollage ? video?.dataset : widget?.dataset;
    const durationInSec = getHMSToSec({ hms: durationInHMS });
    const trimArray = trim ? trim.split(",")?.map(e => parseFloat(e)) : null;
    const isTrimmed = Boolean(trimArray?.length === 2);
    const trimStart = isTrimmed ? trimArray?.[0] : 0;
    const trimEnd = isTrimmed ? trimArray?.[1] : durationInSec;

    return {
      widget,
      video,
      src: widget.dataset.assetType === FRAME ? video.querySelector("source")?.src : video.src,
      poster:
        widget.dataset.assetType === FRAME ? video.querySelector("source").getAttribute("data-poster") : video.poster,
      volume: volume ? parseInt(volume) : 100,
      durationInSec,
      durationInHMS,
      trimStart,
      trimEnd,
      isMuted: Boolean(video.getAttribute("muted")),
      isLooped: Boolean(loop === "true"),
      isTrimmed: Boolean(trimStart > 0 || trimEnd < durationInSec),
    };
  };

  const resumeDomPlayEnv = ({ timer, pageDurationMs, pageId }) => {
    // setting environment for resuming page play where left off (widget show/hide)
    widgets.forEach(w => {
      if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode } = getWidgetData({ widgetObj: w, pageDurationMs });
        widgetNode.style.display = timer >= startTimeMs && timer <= stopTimeMs ? "block" : "none";
      }
    });

    // setting environment for resuming page play where left off (page transition + widget animation)
    document.querySelectorAll(".play-animation").forEach(element => {
      element.style.animationPlayState = "";
    });
  };

  const disableInactivePages = ({ action, pageId }) => {
    if (!isTimeLineViewOpen) {
      if (action === "apply") {
        document.querySelectorAll(".dhp-page-canvas").forEach(page => {
          if (page.id !== pageId) {
            const block = page.querySelector(".dhp-page-block");
            block.classList.add("custom-disable");
          }
        });
      }
      if (action === "revoke") {
        document.querySelectorAll(".dhp-page-block").forEach(block => {
          block.classList.remove("custom-disable");
        });
      }
    }
  };

  const resetTimelineScrollPosition = () => {
    const scrollContainer = document.getElementById("timeline-body");
    const playHead = document.querySelector(".page-timeline-playhead");

    if (!scrollContainer) return;
    if (playHead) {
      // play initiated after pause
      const left = parseInt(playHead.style.left) - PAGE_TIMELINE_LEFT_SECTION;
      scrollContainer.scrollLeft = left;
      scrollContainer.dataset.scrollLeft = left;
    } else {
      // new play
      scrollContainer.scroll(0, 0);
      scrollContainer.dataset.scrollLeft = 0;
    }
  };

  const disableAssetPane = () => {
    const assetWrapper = document.querySelector(".dhp-secondAside .assets-wrapper");
    if (assetWrapper) assetWrapper.style.display = "none";
    // handleWidgetAction("*", false, false, 0, "returnToWidgetList");
  };

  const setDomPlayEnvToCustomTime = ({ pageId, pageObj, pageDurationMs, timer }) => {
    setPageTransitionCurTime({ pageObj, pageDurationMs, timer });

    widgets.forEach(w => {
      if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = getWidgetData({
          widgetObj: w,
          pageDurationMs,
        });

        widgetNode.style.display = timer >= startTimeMs && timer <= stopTimeMs ? "block" : "none";

        if (assetType && startTimeMs < pageDurationMs) {
          setAnimationCurTime({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
        }

        if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source) && startTimeMs < pageDurationMs) {
          setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (assetType === COLLAGE && startTimeMs < pageDurationMs) {
          widgetNode.querySelectorAll("video").forEach(videoNode => {
            setVideoCurTime({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
          });
        }

        if (assetType === FRAME && frameContent === "video" && startTimeMs < pageDurationMs) {
          setVideoCurTime({ widgetNode, timer, startTimeMs, stopTimeMs });
        }
      }
    });
  };

  const stopDomPlay = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.querySelectorAll(".play-animation").forEach(animationNode => {
      animationNode.style.animationPlayState = "paused";
    });
    document.querySelectorAll('video[data-playing="true"]').forEach(videoNode => {
      videoNode.pause();
    });
  };

  const stopPagePlay = ({ action }) => {
    stopDomPlay();
    disableInactivePages({ action: "revoke" });
    setPagePlayData({
      action: null,
      pageId: null,
      pageIdx: null,
      pageTimer: null,
      pageProgress: 0,
      pageDurationMs: 0,
      scrollData: null,
    });

    if (isTimeLineViewOpen === "page-timeline") {
      // applicable only for page play from timeline (page timeline)
      const scrollContainer = document.getElementById("timeline-body");
      if (scrollContainer) {
        scrollContainer.scrollLeft = 0;
        scrollContainer.dataset.scrollLeft = 0;
      }
    }

    if (isTimeLineViewOpen !== "document-timeline") {
      // applicable only for page play (page timeline/page top)
      setPlayingPageIdx(null);

      if (action === "natural_stop") {
        setWhichPagePlaying({
          action: "natural_stop",
          playingPageIdx: pagePlayData.pageIdx,
        });
      }
    }
  };

  const setVideoCurTimeToDefault = ({ pageId, pageIdx }) => {
    setTimeout(() => {
      const pageObj = pageNodes[pageIdx];
      const pageDurationMs = parseFloat(pageObj?.pageDuration ?? 0) * 1000;

      widgets.forEach(w => {
        if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
          const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = getWidgetData({
            widgetObj: w,
            pageDurationMs,
          });

          if (!widgetNode) return;

          if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source)) {
            const { trimStart: trimStartTime } = getVideoNodeAndDataRef({ widgetNode });
            setVideoCurTime({ widgetNode, timer: trimStartTime, startTimeMs, stopTimeMs });
          }

          if (assetType === COLLAGE) {
            widgetNode.querySelectorAll("video").forEach(videoNode => {
              const { trimStart: trimStartTime } = getVideoNodeAndDataRef({ widgetNode, videoNode });
              setVideoCurTime({ widgetNode, videoNode, timer: trimStartTime, startTimeMs, stopTimeMs });
            });
          }

          if (assetType === FRAME && frameContent === "video") {
            const { trimStart: trimStartTime } = getVideoNodeAndDataRef({ widgetNode });
            setVideoCurTime({ widgetNode, timer: trimStartTime, startTimeMs, stopTimeMs });
          }
        }
      });
    }, 30);
  };

  const setVideoCurTime = ({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs }) => {
    const {
      video,
      trimStart: trimStartTime,
      trimEnd: trimStopTime,
    } = getVideoNodeAndDataRef({ widgetNode, videoNode });

    if (timer > startTimeMs && timer < stopTimeMs) {
      // when widget is within playable time range
      const widgetPlayedDurationMs = timer - startTimeMs;
      const videoTrimmedDurationMs = (trimStopTime - trimStartTime) * 1000;
      const videoCurTimeWithinPlayableRange =
        (videoTrimmedDurationMs > widgetPlayedDurationMs
          ? widgetPlayedDurationMs
          : widgetPlayedDurationMs % videoTrimmedDurationMs) / 1000;

      video.currentTime = trimStartTime + videoCurTimeWithinPlayableRange;
    } else {
      video.currentTime = trimStartTime;
    }
  };

  const videoPlay = ({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs }) => {
    const {
      video,
      volume,
      trimStart: trimStartTime,
      trimEnd: trimStopTime,
      isMuted,
      isLooped,
    } = getVideoNodeAndDataRef({ widgetNode, videoNode });
    const videoCurTime = floatToFloatSec(video.currentTime);

    if (timer === startTimeMs) {
      if (isMuted) video.muted = false;
      video.currentTime = trimStartTime;
      video.volume = volume / 100;
      playMedia(video);
      video.setAttribute("data-playing", "true");
    }

    if (timer > startTimeMs && timer < stopTimeMs) {
      if (!(videoCurTime >= trimStartTime && videoCurTime <= trimStopTime)) {
        // when video is outside playable time range
        video.currentTime = trimStartTime;
        if (isLooped) {
          if (video.paused) playMedia(video);
        } else {
          video.pause();
        }
      } else {
        if (video.paused) playMedia(video);
      }
    }

    if (timer === stopTimeMs) {
      video.pause();
      if (isMuted) video.muted = true;
      video.removeAttribute("data-playing");
      video.currentTime = trimStartTime;
    }
  };

  const setAnimationCurTime = ({ widgetNode, widgetObj, startTimeMs, stopTimeMs, timer }) => {
    let activePhase, deactivePhase;
    const _aEngine = animationEngine.utils;
    const {
      animDataObj,
      showEntryAnimation,
      showExitAnimation,
      animationPhase: lastActivePhase,
    } = _aEngine.getWidgetAnimData({
      widgetObj,
      startTimeMs,
      stopTimeMs,
      widgetNode,
    });
    if (!showEntryAnimation && !showExitAnimation) return;

    const animationPipelines = _aEngine.generateAnimPipelines({ animDataObj });

    for (const phase of animationPipelines) {
      if (phase.enabled && timer >= phase.startTime && timer <= phase.stopTime) {
        activePhase = phase;
        break;
      }
    }

    for (const phase of animationPipelines) {
      if (phase.enabled && phase.phaseId !== activePhase && phase.phaseId === lastActivePhase) {
        deactivePhase = phase;
        break;
      }
    }

    _aEngine.seekAnimation({
      activePhase,
      deactivePhase,
      timer,
      widgetNode,
    });
  };

  const animationPlay = ({ widgetNode, widgetObj, startTimeMs, stopTimeMs, timer }) => {
    let activePhase;
    const _aEngine = animationEngine.utils;
    const { animDataObj, showEntryAnimation, showExitAnimation } = _aEngine.getWidgetAnimData({
      widgetObj,
      startTimeMs,
      stopTimeMs,
      widgetNode,
    });
    if (!showEntryAnimation && !showExitAnimation) return;

    const animationPipelines = _aEngine.generateAnimPipelines({ animDataObj });

    for (const phase of animationPipelines) {
      if (phase.enabled && timer >= phase.startTime && timer <= phase.stopTime) {
        activePhase = phase;
        break;
      }
    }
    if (!activePhase) return;

    _aEngine.dispatchAnimation({
      phase: activePhase,
      timer,
      widgetNode,
    });
  };

  const widgetPlay = ({ timer, pageDurationMs, pageId, action }) => {
    // widget play-pause
    widgets.forEach(w => {
      if (w.pageId === pageId && !w.data["data-layer-hidden"]) {
        const { startTimeMs, stopTimeMs, widgetNode, assetType, source, frameContent } = getWidgetData({
          widgetObj: w,
          pageDurationMs,
        });

        if (!widgetNode) return;

        if (timer === startTimeMs) {
          widgetNode.style.display = "block";
        }

        if (assetType && startTimeMs < pageDurationMs) {
          if (action === "force_stop") {
            setAnimationCurTime({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
          }
          animationPlay({ widgetNode, widgetObj: w, startTimeMs, stopTimeMs, timer });
        }

        if (assetType === VIDEO && [STOCK, BRAND, UPLOAD_VIDEO].includes(source) && startTimeMs < pageDurationMs) {
          videoPlay({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (assetType === COLLAGE && startTimeMs < pageDurationMs) {
          widgetNode.querySelectorAll("video").forEach(videoNode => {
            videoPlay({ widgetNode, videoNode, timer, startTimeMs, stopTimeMs });
          });
        }

        if (assetType === FRAME && frameContent === "video" && startTimeMs < pageDurationMs) {
          videoPlay({ widgetNode, timer, startTimeMs, stopTimeMs });
        }

        if (timer === stopTimeMs && stopTimeMs < pageDurationMs) {
          widgetNode.style.display = "none";
        }

        if (timer === pageDurationMs) {
          // page duration end
          widgetNode.style.display = "block";
        }
      }
    });
  };

  const setPageTransitionCurTime = ({ pageObj, pageDurationMs, timer }) => {
    let activePhase;
    const _aEngine = animationEngine.utils;
    const { pageNode, blockNode, animDataObj, showEntryAnimation } = _aEngine.getPageAnimData({
      pageObj,
      pageDurationMs
    });

    // reset playing page props
    pageNode.classList.add("page-playing");
    blockNode.style.pointerEvents = "none";
    blockNode.classList.add("canvas-disabled");
    localStorage.setItem("widget.event.isActive", "true");
    setPlayingPageIdx(pageObj.pageIdx);

    if (!showEntryAnimation) return;

    const animationPipelines = _aEngine.generateAnimPipelines({ animDataObj });

    for (const phase of animationPipelines) {
      if (phase.enabled && timer >= phase.startTime && timer <= phase.stopTime) {
        activePhase = phase;
        break;
      }
    }

    _aEngine.seekAnimation({
      activePhase,
      deactivePhase: null,
      timer,
      pageObj,
    });
  }

  const pageTransitionPlay = ({ pageObj, pageDurationMs, timer }) => {
    let activePhase;
    const _aEngine = animationEngine.utils;
    const { animDataObj, showEntryAnimation } = _aEngine.getPageAnimData({
      pageObj,
      pageDurationMs
    });
    if (!showEntryAnimation) return;

    const animationPipelines = _aEngine.generateAnimPipelines({ animDataObj });

    for (const phase of animationPipelines) {
      if (phase.enabled && timer >= phase.startTime && timer <= phase.stopTime) {
        activePhase = phase;
        break;
      }
    }
    if (!activePhase) return;

    _aEngine.dispatchAnimation({
      phase: activePhase,
      timer,
      pageObj,
    });
  };

  const pagePlay = ({ timer, pageDurationMs, pageObj, pageId, pageIdx, scrollData }) => {
    if (!pageObj) return;
    const pageNode = document.getElementById(pageObj.pageId);
    const blockNode = pageNode.querySelector(".dhp-page-block");
    const progress = customRound((100 / pageDurationMs) * timer, 2);
    const storage_data = operateStorageData({
      type: "session",
      action: "get",
      key: PAGE_PLAY_DATA_STORAGE_KEY,
      isParseable: true,
      defaultData: {
        action: "play",
      },
    });

    setPagePlayData({
      action: timer === pageDurationMs ? "natural_stop" : storage_data.action,
      pageId: pageId ?? storage_data.pageId,
      pageIdx: pageIdx ?? storage_data.pageIdx,
      pageTimer: timer,
      pageProgress: progress,
      pageDurationMs: pageDurationMs,
      scrollData,
    });

    if (timer === 0) {
      // page play start
      pageNode.classList.add("page-playing");
      blockNode.style.pointerEvents = "none";
      blockNode.classList.add("canvas-disabled");
      pageTransitionPlay({ timer, pageDurationMs, pageObj });
      localStorage.setItem("widget.event.isActive", "true");
      if (isTimeLineViewOpen !== "document-timeline") setPlayingPageIdx(pageIdx ?? storage_data.pageIdx);
    }

    if (timer > 0 && timer < pageDurationMs) {
      // page play continues
      pageTransitionPlay({ timer, pageDurationMs, pageObj });
    }

    if (timer === pageDurationMs) {
      // page play stop
      pageNode.classList.remove("page-playing");
      blockNode.style.pointerEvents = "";
      blockNode.classList.remove("canvas-disabled");
      pageTransitionPlay({ timer, pageDurationMs, pageObj });
      localStorage.removeItem("widget.event.isActive");
    }
  };

  useEffect(() => {
    if (action === "play" && !intervalId) {
      let timer = pagePlayData?.pageTimer ?? 0;
      const { pageId, pageIdx, pageObj, pageDurationMs, scrollData } = getPageData();

      if (!pageId || pageIdx < 0 || !pageObj) return;

      resetTimelineScrollPosition();
      disableInactivePages({ action: "apply", pageId });
      resumeDomPlayEnv({ timer, pageDurationMs, pageId });

      if (timer === 0) {
        pagePlay({ timer, pageDurationMs, pageObj, pageId, pageIdx, scrollData });
        widgetPlay({ timer, pageDurationMs, pageId });
      }

      intervalId = setInterval(() => {
        timer += 100;
        widgetPlay({ timer, pageDurationMs, pageId });
        pagePlay({ timer, pageDurationMs, pageObj, pageId, pageIdx, scrollData });
      }, 100);
    }

    if (action === "pause") {
      stopDomPlay();
    }

    if (action === "natural_stop") {
      stopPagePlay({ action });
    }

    if (action === "force_stop") {
      const { pageId, pageIdx, pageObj, pageDurationMs } = getPageData();
      stopPagePlay({ action });
      widgetPlay({ timer: pageDurationMs, pageDurationMs, pageId, pageIdx, action });
      pagePlay({ timer: pageDurationMs, pageDurationMs, pageObj });
    }

    return () => {
      stopDomPlay();
    };
  }, [action]);

  useLayoutEffect(() => {
    if (pagePlayData.action === "play" && isTimeLineViewOpen !== "document-timeline") {
      // reset environments ready for page play
      disableAssetPane();
      updateMetadata({ ...metadata, activeWidgetId: false, activeWidgetType: false });
    }
    setAction(pagePlayData.action);
  }, [pagePlayData.action]);

  useLayoutEffect(() => {
    if (
      whichPagePlaying.action === "pause" &&
      whichPagePlaying.triggerPoint === "slider" &&
      isTimeLineViewOpen !== "document-timeline"
    ) {
      const timer = pagePlayData?.pageTimer ?? 0;
      const { pageId, pageObj, pageDurationMs } = getPageData();
      setDomPlayEnvToCustomTime({ pageId, pageObj, pageDurationMs, timer });
    }
  }, [whichPagePlaying]);

  useLayoutEffect(() => {
    // when not in playing mode, pre-load video at change of active page (so that it reduce buffering time when playing)
    if (metadata.activePageId && !playingPageIdx) {
      setVideoCurTimeToDefault({ pageId: metadata.activePageId, pageIdx: metadata.activePageIdx });
    }
  }, [metadata.activePageId, playingPageIdx]);

  return {
    setVideoCurTimeToDefault,
  };
};

export default usePagePlay;
